/**
 * Review Session API Service
 * Handles performance review sessions, KPA ratings, and discussion responses
 */

import { API_CONFIG } from '@/config/api'
import { authenticatedFetch } from '../authenticatedFetch'

export interface DiscussionPoint {
  id: string
  content: string
  addedBy: 'employee' | 'manager'
  addedByName: string
  timestamp: Date
  /**
   * Soft-delete tombstone. A delete sets this true instead of removing the
   * point, so the backend's delete-wins merge keeps the deletion converged
   * across concurrent employee/manager writers (a stale writer can't resurrect
   * it). Tombstoned points are filtered out of every UI / AI surface.
   */
  deleted?: boolean
}

export interface KPARatingData {
  id?: string
  review_session_id?: string
  kpa_id: string
  kpa_name: string
  rating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  manager_comments?: string
  discussion_responses?: {
    wentWell?: DiscussionPoint[] | string  // Support both new and legacy formats
    challenges?: DiscussionPoint[] | string
    actions?: DiscussionPoint[] | string
    // Legacy fields (deprecated but kept for backwards compatibility)
    recognition?: string
    improvements?: string
    rootCause?: string
  }
  kpi_snapshot?: {
    captured_at: string
    review_period_end: string
    kpis: Array<{
      kpi_id: string
      name: string
      target: number
      current_value: number
      status: string
      as_of_date: string
    }>
  }
}

/**
 * #21 (May 2026 rework): one per-KPA reflection row for a review session.
 * `went_well` / `challenges` map to HR slide 5 — "what was achieved" /
 * "what was not achieved (challenges + root cause)".
 */
export interface KPAReflectionData {
  id: string
  user_id: string
  review_session_id: string
  kpa_id: string
  kpa_name: string | null
  went_well: string | null
  challenges: string | null
  created_at: string
  updated_at: string
}

/** Error carrying the server's own status and refusal text. */
export interface ReviewApiError extends Error {
  status: number
  detail: string
}

export interface RatingCorrectionResult {
  session_id: string
  /** Which value was corrected — decided by the server, not the caller. */
  target: 'initial' | 'calibrated'
  previous_rating: string | null
  rating: string
  history_rows_updated: number
  message: string
}

export interface ReviewSessionData {
  id: string
  user_id: string  // Employee being reviewed
  manager_id: string
  review_type: string
  review_date: string | null
  status: string  // "scheduled" | "in_progress" | "completed" | "pending" etc
  calendar_event_id?: string | null  // Microsoft Graph event ID
  is_formal?: boolean
  requires_rating?: boolean
  initial_rating?: string  // Manager's rating before calibration
  calibrated_rating?: string  // Final rating after calibration
  /**
   * Calibration lock — only populated when getUserReviewCycle is called with
   * includeCalibrationLock=true. `calibration_locked` is true when this formal
   * review's calibration has started or concluded, so reopening is blocked
   * server-side. `calibration_status` is the session phase
   * ('pre-calibration' | 'report-generation' | 'in-progress' | 'completed').
   */
  calibration_locked?: boolean
  calibration_status?: string | null
  /** Single-session read only. Set when the Team Member can only be rated Too Soon To Rate. */
  too_soon_to_rate?: { first_hire: string; tstr_line: string; checkin_end: string } | null
  /** Single-session read only. Set when HR recorded this check-in on the People Leader's behalf. */
  recorded_by_hr?: { at: string | null; reason: string } | null
  /** Single-session read only. True when calibration was held outside the platform and HR recorded it. */
  calibration_recorded_by_hr?: boolean
  meeting_notes?: string
  synthesis_data?: Record<string, any> | null
  completed_date?: string | null
  review_year?: number
  kpa_ratings: KPARatingData[]
  /**
   * Frozen KPA / objective tree at completion. Present on sessions completed
   * after this feature shipped; null on legacy sessions. The read-only
   * workspace prefers this over the live `kpaData` prop so that subsequent
   * edits to objectives don't rewrite history.
   */
  kpa_data_snapshot?: any | null
  created_at: string
  updated_at: string
}

export interface CreateReviewSessionRequest {
  employee_id: string
  manager_id: string
  review_type: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin'
  kpa_ratings?: KPARatingData[]
}

export interface UpdateReviewSessionRequest {
  overall_rating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  overall_comments?: string
  is_complete?: number
  /**
   * Optional snapshot of the KPA / objective tree at the moment the review
   * is finalised. The frontend serialises the live `kpaData` it just
   * reviewed and posts it here; the backend persists it on the session row
   * (`kpa_data_snapshot`). When the session is later reopened in read-only
   * mode this snapshot is replayed so the user sees the objectives /
   * descriptions / KPI targets that were on the page at the time — not
   * whatever the live tree has drifted into. Only meaningful alongside
   * `is_complete: 1`.
   */
  kpa_data_snapshot?: any
}

class ReviewService {
  private baseURL = API_CONFIG.baseURL

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const response = await authenticatedFetch(`${this.baseURL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(options?.headers as Record<string, string>),
      },
    })

    if (!response.ok) {
      // Carry the server's status and detail. This used to throw
      // `HTTP error! status: 409` and discard the body entirely, so every
      // carefully-worded refusal from the API — why a review can't be reopened,
      // why a rating can't be changed right now — was replaced by a status code
      // before it reached the person who needed to read it.
      const raw = await response.text()
      let detail = raw
      try {
        const parsed = JSON.parse(raw)
        detail = typeof parsed?.detail === 'string'
          ? parsed.detail
          : Array.isArray(parsed?.detail)
            ? parsed.detail.map((d: { msg?: string }) => d?.msg).filter(Boolean).join('; ') || raw
            : raw
      } catch {
        /* not JSON — keep the raw body */
      }
      const err = new Error(detail || `Request failed (${response.status})`) as ReviewApiError
      err.status = response.status
      err.detail = detail
      throw err
    }

    return response.status === 204 ? (null as T) : response.json()
  }

  /**
   * Create a new review session
   */
  async createReviewSession(data: CreateReviewSessionRequest): Promise<ReviewSessionData> {
    return this.request<ReviewSessionData>('/performance/reviews/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  }

  /**
   * Get a specific review session by ID
   */
  async getReviewSession(sessionId: string): Promise<ReviewSessionData> {
    return this.request<ReviewSessionData>(`/performance/reviews/sessions/${sessionId}`)
  }

  /**
   * Get all review sessions for a user
   */
  async getUserReviewSessions(
    userId: string,
    reviewType?: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin',
    isComplete?: number,
    reviewYear?: number
  ): Promise<ReviewSessionData[]> {
    const params = new URLSearchParams()
    if (reviewType) params.append('review_type', reviewType)
    if (isComplete !== undefined) params.append('is_complete', isComplete.toString())
    if (reviewYear) params.append('review_year', reviewYear.toString())

    return this.request<ReviewSessionData[]>(
      `/performance/reviews/users/${userId}/sessions${params.toString() ? `?${params.toString()}` : ''}`
    )
  }

  /**
   * Update a review session (overall rating, completion status)
   */
  async updateReviewSession(
    sessionId: string,
    updates: UpdateReviewSessionRequest
  ): Promise<ReviewSessionData> {
    return this.request<ReviewSessionData>(`/performance/reviews/sessions/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  }

  /**
   * Save synthesis data to the review session
   */
  async saveSynthesis(sessionId: string, data: object): Promise<void> {
    await this.request(`/performance/reviews/sessions/${sessionId}/synthesis`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  /**
   * Remove the stored synthesis report from a review session. Participant-guarded
   * on the backend (team member or People Leader only). The report is regenerable.
   */
  async deleteSynthesis(sessionId: string): Promise<void> {
    await this.request(`/performance/reviews/sessions/${sessionId}/synthesis`, {
      method: 'DELETE',
    })
  }

  // -------- B1: Self-Reflection (May 2026 rework) --------
  //
  // The team member's "My Headlines" reflection at check-in prep — one per
  // session. Upsert via PUT. Backend enforces that only the team member can
  // author their own reflection and that the session is still in
  // pending/scheduled (locked once the check-in starts).

  /**
   * Fetch the self-reflection for a review session, or null if none has
   * been saved yet.
   */
  async getSelfReflection(sessionId: string): Promise<{
    id: string
    user_id: string
    review_session_id: string
    headlines_successes: string | null
    headlines_challenges: string | null
    created_at: string
    updated_at: string
  } | null> {
    return this.request(`/performance/reviews/sessions/${sessionId}/self-reflection`)
  }

  /**
   * Upsert the self-reflection. Body fields are merged into the existing
   * row (or a fresh row is created if none exists). Send only the fields
   * you want to change — undefined / omitted fields leave their stored
   * value alone.
   */
  async upsertSelfReflection(
    sessionId: string,
    payload: { headlines_successes?: string | null; headlines_challenges?: string | null },
  ): Promise<{
    id: string
    user_id: string
    review_session_id: string
    headlines_successes: string | null
    headlines_challenges: string | null
    created_at: string
    updated_at: string
  }> {
    return this.request(`/performance/reviews/sessions/${sessionId}/self-reflection`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
  }

  // -------- #21: Per-KPA Reflections (May 2026 rework) --------
  //
  // During check-in prep the team member reflects on EACH KPA in two
  // free-text fields ("what went well" / "challenges + root cause",
  // HR slide 5). One row per (review_session, kpa). Upsert per-KPA via PUT,
  // keyed by kpa_id in the body. Same auth model as the self-reflection: the
  // gateway injects the authenticated user's id; only the team member can
  // author, and edits lock (409) once the check-in leaves pending/scheduled.

  /**
   * Fetch ALL per-KPA reflections for a review session (one row per KPA).
   * Returns an empty array if none have been saved yet. The caller keys
   * these by `kpa_id` to populate each KPA's two textareas.
   */
  async getKPAReflections(sessionId: string): Promise<KPAReflectionData[]> {
    return this.request<KPAReflectionData[]>(
      `/performance/reviews/sessions/${sessionId}/kpa-reflections`
    )
  }

  /**
   * Upsert a single KPA's reflection, keyed by `kpa_id`. Partial merge —
   * send only the field(s) you want to change; null / omitted fields leave
   * their stored value alone (so each textarea can auto-save independently).
   */
  async upsertKPAReflection(
    sessionId: string,
    payload: {
      kpa_id: string
      kpa_name?: string | null
      went_well?: string | null
      challenges?: string | null
    },
  ): Promise<KPAReflectionData> {
    return this.request<KPAReflectionData>(
      `/performance/reviews/sessions/${sessionId}/kpa-reflections`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      }
    )
  }

  /**
   * Export synthesis as PDF
   */
  async exportSynthesisPDF(sessionId: string): Promise<Blob> {
    const response = await authenticatedFetch(
      `${this.baseURL}/performance/reviews/sessions/${sessionId}/synthesis/pdf`,
      { headers: { 'Accept': 'application/pdf' } }
    )
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    return response.blob()
  }

  /**
   * Send synthesis email to employee
   */
  async sendSynthesisEmail(sessionId: string): Promise<{ status: string; to: string }> {
    return this.request(`/performance/reviews/sessions/${sessionId}/synthesis/send`, {
      method: 'POST',
    })
  }

  /**
   * Save or update a KPA rating within a review session
   */
  async saveKPARating(
    sessionId: string,
    ratingData: KPARatingData
  ): Promise<KPARatingData> {
    return this.request<KPARatingData>(
      `/performance/reviews/sessions/${sessionId}/kpa-ratings`,
      {
        method: 'POST',
        body: JSON.stringify(ratingData),
      }
    )
  }

  /**
   * Get the most recent review session for a user (for employee view)
   */
  async getLatestReviewSession(
    userId: string,
    reviewType?: 'year-end' | 'mid-year' | 'q1-checkin' | 'q3-checkin',
    reviewYear?: number
  ): Promise<ReviewSessionData | null> {
    try {
      const sessions = await this.getUserReviewSessions(
        userId,
        reviewType,
        1, // is_complete: only completed reviews
        reviewYear || new Date().getFullYear() // Default to current year
      )
      
      // Return the most recent completed session
      if (sessions.length > 0) {
        return sessions[0] // Already sorted by review_date desc from backend
      }
      
      return null
    } catch (error) {
      console.error('Error fetching latest review session:', error)
      return null
    }
  }

  /**
   * Convert KPA ratings to the format expected by ConductReviewWorkspace
   */
  convertToWorkspaceFormat(session: ReviewSessionData): {
    managerRatings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'>
    managerComments: Record<string, string>
    managerOverallRating?: 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'
  } {
    const managerRatings: Record<string, 'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr'> = {}
    const managerComments: Record<string, string> = {}

    session.kpa_ratings.forEach(rating => {
      if (rating.rating) {
        managerRatings[rating.kpa_id] = rating.rating
      }
      if (rating.manager_comments) {
        managerComments[rating.kpa_id] = rating.manager_comments
      }
    })

    // Use calibrated_rating if available (after calibration), otherwise initial_rating
    const overallRating = (session.calibrated_rating || session.initial_rating) as 
      'exceptional' | 'full' | 'not-yet-full' | 'weak' | 'tstr' | undefined

    return {
      managerRatings,
      managerComments,
      managerOverallRating: overallRating
    }
  }

  /**
   * Initialize review cycle placeholders for an employee
   * Creates Q1, Mid-year, Q3, and Year-end review placeholders
   */
  async initializeReviewCycle(
    userId: string,
    managerId: string,
    reviewYear?: number
  ): Promise<ReviewSessionData[]> {
    return this.request<ReviewSessionData[]>('/performance/reviews/cycle/initialize', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        manager_id: managerId,
        review_year: reviewYear,
      }),
    })
  }

  /**
   * Schedule a specific date/time for a review session
   */
  async scheduleReview(
    sessionId: string,
    scheduledDate: Date,
    scheduledBy: string,
    reviewType: string,
    location: string = 'Virtual (MS Teams)',
    notes?: string,
    durationMinutes?: number
  ): Promise<ReviewSessionData & { notifications?: { calendar_created: boolean; email_sent: boolean; teams_link?: string } }> {
    return this.request<ReviewSessionData & { notifications?: { calendar_created: boolean; email_sent: boolean; teams_link?: string } }>(
      `/performance/reviews/sessions/${sessionId}/schedule`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          scheduled_date: scheduledDate.toISOString(),
          scheduled_by: scheduledBy,
          review_type: reviewType,
          location: location,
          notes: notes,
          duration_minutes: durationMinutes,
        }),
      }
    )
  }

  /**
   * Create a Just-In-Time (JIT) check-in
   */
  async createJITCheckin(data: {
    userId: string
    managerId: string
    reviewType: 'probation' | 'adhoc' | 'performance_improvement'
    scheduledDate?: Date
    description?: string
    initiatedBy?: 'employee' | 'manager'  // Determines meeting organizer
  }): Promise<ReviewSessionData> {
    return this.request<ReviewSessionData>('/performance/reviews/cycle/jit-checkin', {
      method: 'POST',
      body: JSON.stringify({
        user_id: data.userId,
        manager_id: data.managerId,
        review_type: data.reviewType,
        scheduled_date: data.scheduledDate?.toISOString(),
        description: data.description,
        initiated_by: data.initiatedBy || 'employee',
      }),
    })
  }

  /**
   * Get complete review cycle for a user (includes placeholders and JIT check-ins)
   */
  async getUserReviewCycle(
    userId: string,
    reviewYear?: number,
    includeCalibrationLock?: boolean
  ): Promise<ReviewSessionData[]> {
    const params = new URLSearchParams()
    if (reviewYear) params.append('review_year', reviewYear.toString())
    if (includeCalibrationLock) params.append('include_calibration_lock', 'true')

    return this.request<ReviewSessionData[]>(
      `/performance/reviews/users/${userId}/cycle${params.toString() ? `?${params.toString()}` : ''}`
    )
  }

  /**
   * Reschedule a review session
   * @param initiatedBy - 'employee' or 'manager' - determines who becomes the meeting organizer
   */
  async rescheduleReviewSession(
    sessionId: string,
    scheduledDate: Date,
    durationMinutes: number = 30,
    reason?: string,
    initiatedBy: 'employee' | 'manager' = 'employee'
  ): Promise<ReviewSessionData> {
    return this.request<ReviewSessionData>(`/performance/reviews/sessions/${sessionId}/reschedule`, {
      method: 'PATCH',
      body: JSON.stringify({
        scheduled_date: scheduledDate.toISOString(),
        duration_minutes: durationMinutes,
        reason: reason,
        initiated_by: initiatedBy,
      }),
    })
  }

  /**
   * Check calendar availability for attendees at a proposed meeting time
   */
  async checkAvailability(
    attendeeEmployeeIds: string[],
    scheduledDate: Date,
    durationMinutes: number = 60,
    excludeEventId?: string | null
  ): Promise<{
    available: boolean
    conflicts?: Array<{
      email: string
      employee_id?: string
      employee_ids?: string[]
      busy_items: Array<{
        subject: string
        start: string
        end: string
        status: string
      }>
    }>
    error?: string
  }> {
    return this.request<{
      available: boolean
      conflicts?: Array<{
        email: string
        employee_id?: string
        busy_items: Array<{
          subject: string
          start: string
          end: string
          status: string
        }>
      }>
      error?: string
    }>('/performance/reviews/check-availability', {
      method: 'POST',
      body: JSON.stringify({
        attendee_emails: attendeeEmployeeIds,  // Field name is 'attendee_emails' but contains employee IDs
        scheduled_date: scheduledDate.toISOString(),
        duration_minutes: durationMinutes,
        exclude_event_id: excludeEventId,
      }),
    })
  }

  /**
   * Get complete review cycle for a manager's team
   */
  async getManagerTeamReviewCycle(
    managerId: string,
    reviewYear?: number,
    status?: string
  ): Promise<ReviewSessionData[]> {
    const params = new URLSearchParams()
    if (reviewYear) params.append('review_year', reviewYear.toString())
    if (status) params.append('status', status)

    return this.request<ReviewSessionData[]>(
      `/performance/reviews/managers/${managerId}/team-cycle${params.toString() ? `?${params.toString()}` : ''}`
    )
  }

  /**
   * Cancel a JIT check-in session
   * Only non-formal reviews (adhoc, probation, performance_improvement) can be cancelled.
   */
  async cancelReviewSession(
    sessionId: string,
    reason?: string,
    cancelledBy: 'employee' | 'manager' = 'employee'
  ): Promise<ReviewSessionData & { calendar_deleted: boolean }> {
    return this.request<ReviewSessionData & { calendar_deleted: boolean }>(
      `/performance/reviews/sessions/${sessionId}/cancel`,
      {
        method: 'PATCH',
        body: JSON.stringify({
          reason: reason,
          cancelled_by: cancelledBy,
        }),
      }
    )
  }

  /**
   * Sync a review session's calendar status
   * Checks if the calendar event still exists and updates status if cancelled externally
   */
  async syncSessionCalendarStatus(
    sessionId: string
  ): Promise<{
    id: string
    status: string
    sync_result: string
    calendar_exists: boolean | null
    calendar_cancelled?: boolean
    message?: string
  }> {
    return this.request<{
      id: string
      status: string
      sync_result: string
      calendar_exists: boolean | null
      calendar_cancelled?: boolean
      message?: string
    }>(`/performance/reviews/sessions/${sessionId}/sync-calendar`, {
      method: 'POST',
    })
  }

  /**
   * Reopen a COMPLETED review (admin / hr_admin only). Reverts it to
   * IN_PROGRESS so stranded items can be added and the People Leader can
   * re-finalise. The backend clears the frozen kpa_data_snapshot so the
   * reopened review reflects live data; re-completing re-captures it.
   * Gated server-side to admin / hr_admin.
   */
  async reopenReview(
    sessionId: string
  ): Promise<{ session_id: string; status: string; reopened: boolean }> {
    return this.request<{ session_id: string; status: string; reopened: boolean }>(
      `/performance/reviews/sessions/${sessionId}/reopen`,
      { method: 'POST' }
    )
  }

  /**
   * Correct the overall rating on a check-in (admin / hr_admin).
   *
   * Which rating changes is decided server-side — the caller doesn't choose
   * between the People Leader's assessment and the calibrated value. The
   * response reports which lane was written via `target`.
   */
  async correctReviewRating(
    sessionId: string,
    rating: string,
    reason: string
  ): Promise<RatingCorrectionResult> {
    return this.request<RatingCorrectionResult>(
      `/performance/reviews/sessions/${sessionId}/rating`,
      { method: 'PUT', body: JSON.stringify({ rating, reason }) }
    )
  }

  /**
   * Record a check-in held outside the system, on the People Leader's behalf (HRBP,
   * HR admin; HR UAT list #24). Repeatable until the person is submitted to calibration.
   */
  async recordCheckinOnBehalf(
    sessionId: string,
    body: { overall_rating: string; kpa_ratings: Record<string, string>; notes?: string; reason: string; complete: boolean }
  ): Promise<{ session_id: string; status: string; overall_rating: string; message: string }> {
    return this.request(
      `/performance/reviews/sessions/${sessionId}/record-on-behalf`,
      { method: 'PUT', body: JSON.stringify(body) }
    )
  }
}

export const reviewService = new ReviewService()
